import React, { useContext, createContext, useState, useMemo, useCallback } from "react";
import { generateLosingCard, generateWinningCard } from '../utils/card-generator';
import { useVote, useWidget } from '@telescope/cassini-hooks';
import { useAuth } from 'features';
import { useStorage } from 'providers';
import { Tile } from '../types';
import { Closed, Open } from "generated-types/widget";
import { ACTION_TYPES } from "utils";

type BingoContextValue = {
 getCard: () => Tile[][]
 toggleTile: (row: number, col: number) => void,
 onSubmit: () => void,
 selections: number[][]
}

const BingoContext = createContext<BingoContextValue | undefined>(undefined);

export const BingoProvider = ({ children } : { children: React.ReactNode }) => {
  const { data: bingo } = useWidget({ select: (data: Open) => data.snapshot.snapshot_views.bingoOptions });
  const { content: { options }, settings } = bingo!;
  const { data: widget } = useWidget({ select: (data: Open | Closed) => data });
  const { sid } = widget!;
  const loginTelescope = useVote();

  const { user, updateUserVoteHistory } = useAuth();
  const { isWinner } = user!;
  const { user_id, user_email } = user!.user;
  const userId = user_id || user_email;

  const storageKey = useMemo(() => `${sid}_Card_${userId}`, [sid, userId]);
  const storage = useStorage();

  const [ selections, setSelections ] = useState<number[][]>([
    [0,0,0,0,0],
    [0,0,0,0,0],
    [0,0,1,0,0],
    [0,0,0,0,0],
    [0,0,0,0,0]
  ]);

  const getCard = (): Tile[][] => {
    const storedCard = storage.getItem(storageKey);

    if (storedCard) {
      return JSON.parse(storedCard);
    }

    const card = isWinner? generateWinningCard(options, settings.hits, userId) : generateLosingCard(options, settings.hits);
    storage.setItem(storageKey, JSON.stringify(card));
    return card;
  }

  const toggleTile = useCallback((row: number, col: number) => {
    const newSelection = [ ...selections ];
    newSelection[row][col] = selections[row][col] === 0? 1: 0;
    setSelections(newSelection);
  }, [selections]);

  const onSubmit = useCallback(async () => {      
    if (!user || !userId) {
      return Promise.reject('Must be logged in to submit card');
    }
    
    await loginTelescope({
      ...user.user,
      action_type: ACTION_TYPES.SUBMIT,
    });

    updateUserVoteHistory({ submitted: true });
  }, [user, userId, updateUserVoteHistory, loginTelescope]);

  const value = useMemo(() => ({
    getCard,
    toggleTile,
    onSubmit,
    selections
  }), [
    getCard,
    toggleTile,
    onSubmit,
    selections
  ]);

  return <BingoContext.Provider value={value}>{ children }</BingoContext.Provider>
}

export const useBingo = () => {
  const context = useContext(BingoContext);

  if (!context) {
    throw new Error('useBingo must be used within a BingoContext');
  }

  return context;
}